import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  StatusChip,
  TagChip,
  Typography,
  fontStacks,
  palette,
} from '@munifocus/ui';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import type { PropertyHistoryFile } from '@munifocus/contracts-gov';
import { usePropertyHistory } from '../lib/productivity-api';
import { stageLabelKey } from '../lib/file-display';
import { frenchFirstOfMonth } from '@munifocus/utils';

function formatDate(iso: string, locale: string): string {
  return frenchFirstOfMonth(
    new Intl.DateTimeFormat(locale.startsWith('fr') ? 'fr-CA' : 'en-CA', {
      dateStyle: 'medium',
    }).format(new Date(iso)),
  );
}

function HistoryRow({ file, locale }: { file: PropertyHistoryFile; locale: string }) {
  const { t } = useTranslation();
  const reference = (
    <Typography
      sx={{ fontFamily: fontStacks.mono, fontSize: 13, fontWeight: 600, color: 'inherit' }}
    >
      {file.reference}
    </Typography>
  );
  return (
    <Box
      component="li"
      sx={{ py: 1.25, borderTop: `1px solid ${palette.border.line}`, listStyle: 'none' }}
    >
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        {file.redacted ? (
          reference
        ) : (
          <Box
            component={RouterLink}
            to={`/files/${file.id}`}
            aria-label={`${file.reference} — ${file.address}`}
            sx={{
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {reference}
          </Box>
        )}
        <StatusChip
          status={file.open ? 'pending' : 'neutral'}
          label={file.open ? t('gov.propertyHistory.open') : t('gov.propertyHistory.closed')}
        />
      </Stack>
      <Typography variant="body2" sx={{ mt: 0.25 }}>
        {file.address}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t(stageLabelKey(file.stage))} ·{' '}
        {t('gov.propertyHistory.opened', { date: formatDate(file.createdAt, locale) })}
        {!file.redacted && file.ownerName ? ` · ${file.ownerName}` : ''}
      </Typography>
      <Stack direction="row" spacing={0.5} useFlexGap sx={{ mt: 0.5, flexWrap: 'wrap' }}>
        {file.match.map((m) => (
          <TagChip key={m} label={t(`gov.propertyHistory.match.${m}`)} />
        ))}
        {file.redacted && <TagChip label={t('gov.propertyHistory.limitedView')} />}
      </Stack>
    </Box>
  );
}

function Group({
  title,
  files,
  empty,
  locale,
}: {
  title: string;
  files: PropertyHistoryFile[];
  empty: string;
  locale: string;
}) {
  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="subtitle2">{title}</Typography>
      {files.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {empty}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, mt: 0.5, p: 0 }}>
          {files.map((f) => (
            <HistoryRow key={f.id} file={f} locale={locale} />
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * Prior and open files at the same property, and the owner's other properties.
 *
 * Everything comes back already narrowed: a file the officer cannot reach is
 * absent, and one they may only see as the public does (a limited-licence seat)
 * arrives `redacted` — printed without its owner and without a link into the
 * record, with a note saying why.
 */
export function PropertyHistoryPanel({ fileId }: { fileId: string }) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError } = usePropertyHistory(fileId);

  return (
    <Box>
      <Typography variant="overline">{t('gov.propertyHistory.title')}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t('gov.propertyHistory.hint')}
      </Typography>
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      {isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {t('gov.propertyHistory.loadError')}
        </Alert>
      )}
      {data && (
        <>
          <Group
            title={t('gov.propertyHistory.sameProperty')}
            files={data.sameProperty}
            empty={t('gov.propertyHistory.noSameProperty')}
            locale={i18n.language}
          />
          <Group
            title={t('gov.propertyHistory.ownerProperties')}
            files={data.ownerProperties}
            empty={t('gov.propertyHistory.noOwnerProperties')}
            locale={i18n.language}
          />
          {data.sameProperty.some((f) => f.redacted) && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {t('gov.propertyHistory.limitedNote')}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}
